import BadgeDetached from "@entrilia/shared/components/BadgeDetached";
import StatusChip from "@entrilia/shared/components/StatusChip";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import type { SxProps, Theme } from "@mui/material";
import { Box, IconButton, ListItemText, MenuItem, menuItemClasses } from "@mui/material";
import type { MouseEvent } from "react";
import type { AssistantChat, AssistantRunStatus } from "../../../api/types/assistantTypes";
import { isRunPending } from "./chatState";
import RunStatusChip from "./RunStatusChip";

interface Props {
  chat: AssistantChat;
  selected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

const DELETE_BUTTON_CLASS = "chat-list-row-delete";

const rowSx: SxProps<Theme> = {
  gap: 1,
  minHeight: { sm: 42 },
  [`& .${DELETE_BUTTON_CLASS}`]: { display: "none" },
  [`&:hover, &.${menuItemClasses.focusVisible}, &:has(:focus-visible)`]: {
    [`& .${DELETE_BUTTON_CLASS}`]: { display: "inline-flex" },
  },
};

const ChatStatusChip = ({ status }: { status: AssistantRunStatus | undefined }) => {
  if (!status || (status !== "WaitingForApproval" && !isRunPending(status))) {
    return null;
  }

  return (
    <Box sx={{ flexShrink: 0 }}>
      <RunStatusChip status={status} height="smaller" />
    </Box>
  );
};

const ChatListRow = ({ chat, selected, onSelect, onDelete }: Props) => {
  const handleDeleteClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete();
  };

  return (
    <MenuItem selected={selected} onClick={onSelect} sx={rowSx}>
      <ListItemText
        primary={chat.title || "New chat"}
        secondary={
          chat.taskName && (
            <StatusChip
              label={chat.taskName}
              tooltipText={`Task: ${chat.taskName}`}
              color={(t) => t.palette.text.secondary}
              neutral
              height="smaller"
            />
          )
        }
        slotProps={{ primary: { noWrap: true }, secondary: { component: "div" } }}
      />
      {chat.hasUnreadUpdates && !selected && <BadgeDetached variant="dot" color="info" />}
      <ChatStatusChip status={chat.status} />
      <IconButton className={DELETE_BUTTON_CLASS} edge="end" aria-label="Delete chat" onClick={handleDeleteClick}>
        <DeleteIcon />
      </IconButton>
    </MenuItem>
  );
};

export default ChatListRow;
